import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import toArray from "lodash/toArray";

import differenceBy from "lodash/differenceBy";

import {
  extractSurveyPageData,
  segregateQuestions,
  triggeredQuestions
} from "@/helpers/survey";
import { hydrate } from "@/lib";
import type {
  Question,
  QuestionOption,
  Statements,
  Survey,
  SurveyPage,
  ResponsePayload
} from "@/types";
import {
  fetchQuestionResult,
  fetchSurvey,
  fetchSurveyPage,
  fetchSurveyResponses,
  saveSurvey
} from "./thunk";

export interface SurveyState {
  survey: Partial<Survey>;
  surveyPage: Array<string>;
  fetching: boolean;
  fetchError: string;
  saving: boolean;
  saveError: string;
  answers: { id?: string | number };
  pages: Array<SurveyPage>;
  questions: { [key: string]: Question };
  statements: { [key: string]: Statements };
  options: { [key: string]: QuestionOption };
  errors: { [key: string]: { code?: number } };
  viewResponse: boolean;
  taken: boolean;

  responses: { [key: string]: any };
  status: "idle" | "fetching" | "saving";
  error: string | null;
  results: { [Key: string]: { [key: number]: number } };
}

const initialState: SurveyState = {
  survey: {},
  surveyPage: [],
  fetching: false,
  fetchError: "",
  saving: false,
  saveError: "",
  answers: {},
  pages: [],
  questions: {},
  statements: {},
  options: {},
  errors: {},
  viewResponse: false,
  taken: false,
  responses: {},
  status: "idle",
  error: null,
  results: {}
};

export const surveySlice = createSlice({
  name: "survey",
  initialState,
  reducers: {
    resetSurvey: (state) => {
      const pages = state.pages;
      const firstPage = pages.find((page) => page.first);

      state.pages = pages.map((page) => ({
        ...page,
        current: page.id === `${firstPage.id}`
      }));

      state.errors = {};
      state.answers = {};
      state.saveError = "";
      state.fetchError = "";
      state.viewResponse = false;
    },
    toggleResponse: (state) => {
      const pages = state.pages;
      state.viewResponse = !state.viewResponse;

      const firstPage = pages.find((page) => page.first);

      state.pages = pages.map((page) => ({
        ...page,
        current: page.id === `${firstPage.id}`
      }));

      if (state.viewResponse === false) {
        state.answers = initialState.answers;
        state.results = initialState.results;
      }
    },
    generateSurveyData: (state) => {
      const { pages, questions, statements, options } = extractSurveyPageData(
        state.surveyPage
      );
      state.pages = pages;
      state.questions = questions;
      state.statements = statements;
      state.options = options;
    },
    setSurveyErrors: (state, action) => {
      const { questionId, error } = action.payload;

      state.errors = {
        ...state.errors,
        [questionId]: error
      };
    },
    setSurveyAnswers: (state, action) => {
      const { questionId, answer } = action.payload;
      const question = state.questions[questionId];

      state.saveError = "";
      state.fetchError = "";
      delete state.errors[questionId];

      if (
        ["RadioButtonQuestion", "CheckBoxQuestion", "DropDownQuestion"].indexOf(
          question?.type
        ) > -1
      ) {
        if (state.answers[questionId]) {
          const questionsInPage = toArray(state.questions).filter(
            (q) => q.pageId === question.pageId
          );

          const optionsInPage = toArray(state.options).filter((o) =>
            questionsInPage.some((q) => q.id === o.questionId)
          );

          const { conditionalQuestions } = segregateQuestions(
            questionsInPage,
            optionsInPage
          );

          const findDescendants = (
            question: Partial<Question>,
            descendants: Array<unknown>
          ) => {
            const { answers } = state;
            if (answers[question.id] && conditionalQuestions[question.id]) {
              const children = triggeredQuestions(
                answers[question.id],
                conditionalQuestions[question.id]
              );
              descendants.push(...children);
              children.forEach((child) => {
                findDescendants(child, descendants);
              });
            }
          };

          const previouslyTriggered = [];
          findDescendants(question, previouslyTriggered);

          state.answers = {
            ...state.answers,
            [questionId]: answer
          };

          const newlyTriggered = [];
          findDescendants(question, newlyTriggered);

          differenceBy(previouslyTriggered, newlyTriggered, "id").forEach(
            (q) => {
              delete state.answers[q.id];
            }
          );
        } else {
          state.answers = {
            ...state.answers,
            [questionId]: answer
          };
        }
      } else {
        state.answers = {
          ...state.answers,
          [questionId]: answer
        };
      }
    },
    removeSurveyAnswers: (state, action) => {
      const { questionId } = action.payload;
      delete state.answers[questionId];
    },
    removeSurveyLikertAnswers: (state, action) => {
      const { questionId, statementId } = action.payload;
      delete state.answers[questionId][statementId];
    },
    removeSurveyErrors: (state, action) => {
      const { questionId } = action.payload;
      delete state.errors[questionId];
    },
    setSurveyPage: (state, action) => {
      const pages = state.pages;
      const { pageId } = action.payload;
      const currentPage = pages.find((page) => page.current);

      state.pages = pages.map((page) => ({
        ...page,
        current: page.id === `${pageId}`,
        ...(!page.first && {
          previous:
            page.id === `${pageId}` ? `${currentPage.id}` : page.previous
        })
      }));
    },
    paginateBackwards: (state, action) => {
      const pages = state.pages;
      const { pageId } = action.payload;
      const currentPage = pages.find((page) => page.current);

      state.saveError = initialState.saveError;
      state.pages = pages.map((page) => ({
        ...page,
        current: page.id === pageId
      }));

      for (const question in state.questions) {
        if (
          state.questions[question].skip &&
          state.questions[question].pageId === currentPage.id
        ) {
          state.questions = {
            ...state.questions,
            [state.questions[question].id]: {
              ...state.questions[question],
              skip: false
            }
          };
        }
      }
    },
    setEhqSkipQuestion: (state, action: PayloadAction<string | number>) => {
      const { questions } = state;

      state.questions = {
        ...questions,
        [action.payload]: {
          ...questions[action.payload],
          skip: true
        }
      };
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(hydrate, (state, action: PayloadAction<string>) => ({
        ...state,
        ...action.payload[surveySlice.name]
      }))
      .addCase(fetchSurvey.pending, (state) => {
        state.fetching = true;
      })
      .addCase(fetchSurvey.fulfilled, (state, action) => {
        state.fetching = false;
        state.survey = action.payload;
      })
      .addCase(fetchSurvey.rejected, (state, action) => {
        state.fetching = false;
        state.fetchError = action.error.message;
      })
      .addCase(fetchSurveyPage.pending, (state) => {
        state.fetching = true;
      })
      .addCase(fetchSurveyPage.fulfilled, (state, action) => {
        state.fetching = false;
        state.surveyPage = action.payload;
      })
      .addCase(fetchSurveyPage.rejected, (state, action) => {
        state.fetching = false;
        state.fetchError = action.error.message;
      })
      .addCase(saveSurvey.pending, (state) => {
        state.saving = true;
      })
      .addCase(saveSurvey.fulfilled, (state, action) => {
        state.saving = false;
        state.responses = { ...state.responses, ...action.payload };
      })
      .addCase(saveSurvey.rejected, (state, action) => {
        state.saving = false;
        state.saveError = action.error.message;
      })
      .addCase(fetchSurveyResponses.pending, (state) => {
        state.status = "fetching";
      })
      .addCase(fetchSurveyResponses.fulfilled, (state, action) => {
        state.status = "idle";
        state.taken = action.payload?.completedResponse ?? false;
        state.responses = action.payload;
        state.answers = action.payload?.completedResponse
          ? state.answers
          : action.payload?.answers;
      })
      .addCase(fetchSurveyResponses.rejected, (state, action) => {
        const { statusText } = action.payload as ResponsePayload;

        state.status = "idle";
        state.error = statusText;
      })
      .addCase(fetchQuestionResult.pending, (state) => {
        state.status = "fetching";
      })
      .addCase(fetchQuestionResult.fulfilled, (state, action) => {
        state.status = "idle";
        state.results = { ...state.results, ...action.payload };
      })
      .addCase(fetchQuestionResult.rejected, (state, action) => {
        const { statusText } = action.payload as ResponsePayload;

        state.status = "idle";
        state.error = statusText;
      });
  }
});

export const {
  resetSurvey,
  setSurveyPage,
  toggleResponse,
  setSurveyErrors,
  setSurveyAnswers,
  removeSurveyAnswers,
  paginateBackwards,
  setEhqSkipQuestion,
  generateSurveyData,
  removeSurveyErrors,
  removeSurveyLikertAnswers
} = surveySlice.actions;
